import {
  CalendarDays,
  CircleDollarSign,
  LayoutGrid,
  MonitorPlay,
  Music2,
  PenLine,
  Share2,
  ShieldCheck,
  TrendingUp,
  Users,
} from 'lucide-react';

import type { PermissionLevel } from '@qlk/shared';

import { coQuyen } from '../lib/auth.js';
import { MODULE_DA_CAI, NHOM_MENU, type KhaiBaoNav, type NhomMenu } from '../lib/modules.js';

/**
 * Menu chuẩn theo thiết kế đã duyệt (xem `docs/ui.md`).
 *
 * Module nào đã cài (có `src/modules/<id>/module.ts` khai báo `nav`) thì dùng
 * khai báo của chính nó. Module chưa làm vẫn hiện trong menu nhưng ở dạng mờ,
 * không bấm được — nhờ vậy khung giao diện giống thiết kế ngay từ module 00.
 *
 * **Mục con đã ẩn** ở bước này (route vẫn chạy, vào được bằng URL; bước 3 sẽ
 * gộp chúng thành trang con): `/nhom-doi-ngu` và `/nhan-su/ho-so-thue` gộp vào
 * "Nhân sự & nhóm", `/ket-noi-api` gộp vào "Cài đặt".
 */
export const MENU_CHUAN: KhaiBaoNav[] = [
  {
    group: 'VẬN HÀNH',
    label: 'Tổng quan',
    icon: LayoutGrid,
    order: 1,
    permission: 'dashboard',
    to: '/',
  },
  {
    group: 'VẬN HÀNH',
    label: 'Kênh',
    icon: MonitorPlay,
    order: 2,
    permission: 'channels',
    badge: 'so-kenh',
    to: '/kenh',
  },
  {
    group: 'VẬN HÀNH',
    label: 'Network',
    icon: Share2,
    order: 3,
    permission: 'networks',
    to: '/network',
  },
  {
    group: 'VẬN HÀNH',
    label: 'Hiệu suất',
    icon: TrendingUp,
    order: 4,
    permission: 'analytics',
    to: '/phan-tich',
  },

  {
    group: 'NỘI DUNG',
    label: 'Sản xuất',
    icon: PenLine,
    order: 1,
    permission: 'production',
    to: '/san-xuat',
  },
  {
    group: 'NỘI DUNG',
    label: 'Lịch đăng',
    icon: CalendarDays,
    order: 2,
    permission: 'schedule',
    to: '/lich-dang',
  },
  {
    group: 'NỘI DUNG',
    label: 'Kho nhạc',
    icon: Music2,
    order: 3,
    permission: 'music',
    to: '/kho-nhac',
  },

  {
    group: 'TÀI CHÍNH',
    label: 'Doanh thu',
    icon: CircleDollarSign,
    order: 1,
    permission: 'revenue',
    to: '/doanh-thu',
  },

  {
    group: 'QUẢN TRỊ',
    label: 'Nhân sự & nhóm',
    icon: Users,
    order: 1,
    permission: 'team',
    to: '/nhan-su',
  },
  {
    group: 'QUẢN TRỊ',
    label: 'Bản quyền',
    icon: ShieldCheck,
    order: 2,
    permission: 'compliance',
    to: '/ban-quyen',
  },
];

export interface MucMenu extends KhaiBaoNav {
  to: string;
  /** Module chưa được cài: hiện mờ, không bấm được. */
  chuaCai: boolean;
}

/**
 * Mục menu này có hiện với người dùng hiện tại hay không.
 *
 * Một nơi duy nhất cho cả ba luật, vì trước 18/10/2026 `Sidebar` hỏi thẳng
 * `coQuyen(permissions, m.permission, m.mucQuyen ?? 'view')` và hai mục bị ẩn
 * oan: "Thu nhập của tôi" (route chỉ đòi đăng nhập, menu lại đòi `core:view`)
 * và "Cài đặt" (khung chứa mục của nhiều module, menu lại đòi `team:admin`).
 *
 * Thứ tự: `luonHien` → `quyenBatKy()` → `permission` + `mucQuyen`.
 */
export function hienMucMenu(
  permissions: Record<string, PermissionLevel> | undefined,
  muc: Pick<MucMenu, 'permission' | 'mucQuyen' | 'luonHien' | 'quyenBatKy'>,
): boolean {
  if (muc.luonHien === true) return true;

  if (muc.quyenBatKy) {
    const ds = muc.quyenBatKy();
    if (ds.length > 0) return ds.some((q) => coQuyen(permissions, q.module, q.level));
  }

  return coQuyen(permissions, muc.permission, muc.mucQuyen ?? 'view');
}

/** Gộp menu chuẩn với khai báo thật của các module đã cài. */
export function dungMenu(): Record<NhomMenu, MucMenu[]> {
  const navDaCai = new Map(
    MODULE_DA_CAI.filter((m) => m.nav).map((m) => [m.nav!.permission, { nav: m.nav!, module: m }]),
  );

  // Đường dẫn đã có người nhận (module nào đó đăng ký route), kể cả module core.
  const duongDanDaCo = new Set(MODULE_DA_CAI.flatMap((m) => m.routes ?? []).map((r) => r.path));

  const tatCa: MucMenu[] = MENU_CHUAN.map((chuan) => {
    const that = navDaCai.get(chuan.permission);
    const nav = that?.nav ?? chuan;
    const to = nav.to ?? that?.module.routes?.[0]?.path ?? chuan.to ?? '/';
    return { ...nav, to, chuaCai: !that && !duongDanDaCo.has(to) };
  });

  // Module đã cài nhưng không nằm trong menu chuẩn thì vẫn hiện.
  for (const [permission, { nav, module }] of navDaCai) {
    if (MENU_CHUAN.some((c) => c.permission === permission)) continue;
    tatCa.push({ ...nav, to: nav.to ?? module.routes?.[0]?.path ?? '/', chuaCai: false });
  }

  // Mục menu thứ hai trở đi của một module (xem `navThem`).
  for (const module of MODULE_DA_CAI) {
    for (const nav of module.navThem ?? []) {
      tatCa.push({ ...nav, to: nav.to ?? module.routes?.[0]?.path ?? '/', chuaCai: false });
    }
  }

  /* Dựng từ `NHOM_MENU` để thêm/đổi nhóm chỉ phải sửa một chỗ. */
  const theoNhom = Object.fromEntries(NHOM_MENU.map((n) => [n, [] as MucMenu[]])) as Record<
    NhomMenu,
    MucMenu[]
  >;

  for (const muc of tatCa) theoNhom[muc.group].push(muc);
  for (const nhom of Object.values(theoNhom)) nhom.sort((a, b) => a.order - b.order);

  return theoNhom;
}
